import { useQuery } from '@tanstack/react-query';
import {
  ICCAsset,
  IProduct,
  IProductVariation,
  ITemplate,
  IThemeRes,
  IVariationTemplate,
  IVariationTemplateVariation,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapProductVariationSizes } from './helper';

const DEFAULT_PRODUCT_INCLUDES = [
  'products',
  'variationTemplates',
  'variationTemplateVariations',
  'templates',
];

export const useGetProduct = (
  productCode: string,
  includes: string[] = DEFAULT_PRODUCT_INCLUDES,
) => {
  return useQuery({
    queryKey: queryKeys.products.details([productCode, ...includes]),
    queryFn: () => fetchProduct(productCode, includes),
    enabled: !!productCode,
  });
};

const VARIATION_TEMPLATE_INCLUDES = [
  'variationTemplates',
  'variationTemplateVariations',
  'templates',
];

const SIZE_FLAVOR_INCLUDES = [
  'productVariationSizes',
  'productVariationFlavors',
];

const fetchProduct = async (
  productCode: string,
  includes: string[] = DEFAULT_PRODUCT_INCLUDES,
): Promise<IProduct> => {
  const url = `/productVariations${generateQuery(includes, [
    'pageSize=0',
    `group_product_code.list=${productCode}`,
    'sort=created_at',
  ])}`;
  const response = await requestCCApi({ url });
  const product = response.includes?.products?.data[0];
  const variations = response.data;

  const hasTemplateIncludes = VARIATION_TEMPLATE_INCLUDES.every((inc) =>
    includes.includes(inc),
  );
  const hasSizeFlavorIncludes = SIZE_FLAVOR_INCLUDES.every((inc) =>
    includes.includes(inc),
  );

  let productVariations = variations;

  if (hasTemplateIncludes) {
    productVariations = mapProductVariationsWithTemplate(
      variations,
      response.includes?.variationTemplates?.data as IVariationTemplate[],
      response.includes?.variationTemplates?.includes
        ?.variationTemplateVariations?.data as IVariationTemplateVariation[],
      response.includes?.variationTemplates?.includes?.templates
        ?.data as ITemplate[],
    );
  } else if (hasSizeFlavorIncludes) {
    productVariations = mapProductVariationSizes(
      variations,
      response.includes?.productVariationSizes?.data,
      response.includes?.productVariationFlavors?.data,
    );
  }

  // Map variation-level assets from includes to each variation
  const allAssets: ICCAsset[] = response.includes?.assets?.data ?? [];
  if (allAssets.length > 0) {
    productVariations = productVariations.map((v: IProductVariation) => ({
      ...v,
      assets: allAssets.filter((a) => v.asset_id?.includes(a.id)),
    }));
  }

  const productAssets: ICCAsset[] =
    response.includes?.products?.includes?.assets?.data ?? [];
  const themes: IThemeRes[] =
    response.includes?.assets?.includes?.themes?.data ?? [];

  return {
    ...product,
    productVariations,
    assets: productAssets.filter((asset) =>
      product?.asset_id?.includes(asset.id),
    ),
    themes,
  };
};

export const mapProductVariationsWithTemplate = (
  productVariations: any[],
  variationTemplates: IVariationTemplate[] = [],
  variationTemplateVariations: IVariationTemplateVariation[] = [],
  templates: ITemplate[] = [],
): IProductVariation[] => {
  return productVariations.map((productVariation) => {
    const variationTemplate = variationTemplates.find(
      (vt) => vt.id === productVariation?.variation_template_id[0],
    );
    const currentVariationTemplateVariations =
      variationTemplateVariations.filter(
        (vtv) =>
          vtv.variation_template_id ===
          productVariation?.variation_template_id[0],
      );
    const template = templates.find(
      (template) => template.id === variationTemplate?.template_id,
    );

    const mainVariation = [
      {
        order: 1,
        product_code: variationTemplate?.product_code,
        config: variationTemplate?.config,
      },
    ];
    const templateVariations = currentVariationTemplateVariations.map(
      (vtv) => ({
        order: vtv.order,
        product_code: vtv.product_code,
        config: vtv.config,
      }),
    );

    return {
      ...productVariation,
      variation_template:
        productVariation?.variation_template_id.length === 0
          ? null
          : {
              id: productVariation.variation_template_id[0],
              template_id: template?.id,
              template_name: template?.name,
              variations: mainVariation
                .concat(templateVariations)
                .sort((a, b) => a.order - b.order),
            },
    };
  });
};
